import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import translationEn from './langs/en';
import translationEl from './langs/el';
import { getExplicitLanguageFromPathname } from '../lib/language.js';
import { hasLocalizedCounterpart } from '../content/pages/registry.js';

const detector = new LanguageDetector();

// The URL path is the language authority.
//
//   /el/...            Greek, for everyone.
//   /wedding-planning  English, for everyone — it has a Greek URL to be Greek
//                      at, so this one is not it.
//
// Both halves matter. The first is what lets a Greek URL be indexed as Greek.
// The second is what stops a stored preference or a `?lang=el` from putting
// Greek copy on an English URL, which is how the two versions of a page used to
// be one URL as far as a crawler was concerned.
//
// It is decided HERE, in the detector, rather than corrected after mount: the
// static document is English, so an i18next that initialised to Greek would
// render Greek and then be flipped back, and the visitor would see the flash.
// The `data-prerender-lang` guard existed to hide exactly that flash, and it is
// gone because this replaces the need for it rather than covering it up.
//
// A page with NO Greek URL returns undefined and falls through to the detectors
// below, so `?lang=el` and a saved choice still reach the Greek copy that has
// nowhere else to live — the guides index, today. See `hasLocalizedCounterpart`
// for why that is a page-level question and not a site-level one.
detector.addDetector({
  name: 'pathname',
  lookup() {
    if (typeof window === 'undefined') return undefined;
    const pathname = window.location.pathname;
    const language = getExplicitLanguageFromPathname(pathname);
    if (language === 'el') return 'elGR';
    if (language === 'en') return 'enUS';
    return hasLocalizedCounterpart(pathname) ? 'enUS' : undefined;
  },
});

// ?lang=en|el override — wins over localStorage and navigator so deep
// links (e.g. from the app or shared URLs) can force the marketing site
// into a specific language regardless of cached choice or browser locale.
detector.addDetector({
  name: 'querystringMapped',
  lookup() {
    if (typeof window === 'undefined') return undefined;
    const raw = new URLSearchParams(window.location.search).get('lang');
    if (!raw) return undefined;
    const lower = raw.toLowerCase();
    if (lower.startsWith('el')) return 'elGR';
    if (lower.startsWith('en')) return 'enUS';
    return undefined;
  },
});

i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources: {
      enUS: { translation: translationEn },
      elGR: { translation: translationEl },
    },
    supportedLngs: ['enUS', 'elGR'],
    fallbackLng: 'enUS',
    ns: ['translation'],
    defaultNS: 'translation',
    detection: {
      // Order is intentional: the pathname wins over everything, then
      // `?lang=`, then a previously saved choice which persists across visits.
      // The browser/system language is deliberately NOT consulted — the
      // marketing site defaults to English unless the URL or a prior explicit
      // choice says otherwise, and it never redirects by `Accept-Language`.
      //
      // The last two now only ever get a turn on a page with no localized URL.
      // On every bilingual page the pathname answers first, in both directions,
      // which is what "the URL is the language" means in practice.
      order: ['pathname', 'querystringMapped', 'localStorage'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    debug: false,
  });

export default i18n;
